import { MouseEventHandler } from "react";
import { Button } from "react-bootstrap";
import * as Icons from "../Icons";
import type { IconName } from "../Icons";
import { getIconSize } from "./ButtonUtils";

export type ButtonSize = "small" | "large" | "normal";
type ButtonType = "button" | "submit" | "reset";
/**
 * Icon names accepted by `leftIcon` / `rightIcon`. Comes from the
 * `<Icon>` registry rather than `keyof typeof Icons` directly so
 * non-icon exports from the barrel (the `Icon` wrapper, the
 * `IconName` type itself) don't accidentally surface as valid names.
 */
type IconType = IconName;

type ButtonIconProps = {
  id: string;
  /**
   * Explicit `| undefined` on optional props because callers pass
   * `isMobile ? undefined : "Label"` style ternaries, which TS with
   * `exactOptionalPropertyTypes: true` rejects against bare `?:`.
   */
  label?: string | undefined;
  leftIcon?: IconType | undefined;
  rightIcon?: IconType | undefined;
  size?: ButtonSize | undefined;
  iconSize?: number | undefined;
  disabled?: boolean | undefined;
  active?: boolean | undefined;
  count?: number | undefined;
  className?: string | undefined;
  type?: ButtonType | undefined;
  onClick?: MouseEventHandler | undefined;
  loading?: boolean | undefined;
  href?: string | undefined;
  /**
   * Native HTML `form="..."` attribute. Lets a submit button live
   * outside the form it submits (e.g. a Dialog.Footer button that
   * submits a form rendered in Dialog.Body) without resorting to
   * imperative `document.getElementById(...).requestSubmit()`.
   */
  form?: string | undefined;
};

function ButtonIcon({
  label,
  leftIcon,
  rightIcon,
  size = "small",
  count = 0,
  className,
  iconSize,
  href,
  disabled,
  active,
  type = "button",
  onClick,
  loading,
  id,
  form,
}: ButtonIconProps) {
  const fixedSizeIcon = iconSize || getIconSize(size);
  const LeftIconComponent = leftIcon ? Icons[leftIcon] : null;
  const RightIconComponent = rightIcon ? Icons[rightIcon] : null;
  const buttonClasses = ["button-icon"];
  if (LeftIconComponent) {
    buttonClasses.push("has-left-icon");
  }
  if (RightIconComponent) {
    buttonClasses.push("has-right-icon");
  }
  if (!label) {
    buttonClasses.push("has-no-label");
    if (count) {
      buttonClasses.push("has-counter");
    }
  }

  className && buttonClasses.push(className);

  // react-bootstrap's ButtonProps declares optional props as `prop?: T`
  // (without `| undefined`), which exactOptionalPropertyTypes treats as
  // "may be absent" rather than "may be undefined". Build the props
  // conditionally so we never pass an explicit `undefined` value.
  const buttonProps: Record<string, unknown> = {
    id,
    type,
    className: buttonClasses.join(" ").trim(),
    disabled: disabled ? disabled : loading,
  };
  if (href !== undefined) buttonProps.href = href;
  if (active !== undefined) buttonProps.active = active;
  if (onClick !== undefined) buttonProps.onClick = onClick;
  if (form !== undefined) buttonProps.form = form;

  return (
    <Button {...buttonProps}>
      {LeftIconComponent && (
        <span className="icon icon-left">
          <LeftIconComponent size={fixedSizeIcon} color="currentColor" />
          {!label && count !== 0 && <span className="count">{count}</span>}
        </span>
      )}
      {label && <span className="text">{label}</span>}
      {RightIconComponent && (
        <span className="icon icon-right">
          {<RightIconComponent size={fixedSizeIcon} color="currentColor" />}
          {!label && count !== 0 && <span className="count">{count}</span>}
        </span>
      )}
    </Button>
  );
}

export default ButtonIcon;
